import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Table, 
    Typography, 
    Tag, 
    Space, 
    Button,
    message,
    Spin,
    Select,
    DatePicker,
    Flex,
    Progress} from 'antd';
import { 
    CloseCircleOutlined,
    ReloadOutlined,
    DownloadOutlined,
    CalendarOutlined,
    PlusOutlined,
    MinusOutlined} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useGroups } from '../../hooks/useGroups';
import { 
    fetchStudentPackageReportList,
    clearStudentPackageReport 
} from '../../store/slices/reportsSlice';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PackageReport = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const { studentPackageReport } = useSelector((state) => state.reports);
    const { ageGroups } = useAgeGroups(selectedBranch?.id);
    const { groups } = useGroups(selectedBranch?.id);
    
    const [filters, setFilters] = useState({
        dateRange: [dayjs().subtract(1, 'year').startOf('month'), dayjs().endOf('month')],
        selectedGroup: null,
        selectedAgeGroup: null
    });

    // Raporu yükle
    const loadReport = async () => {
        if (!selectedBranch) {
            message.error('Lütfen önce bir branş seçiniz!');
            return;
        }

        // Grup veya yaş grubu seçili değilse uyarı ver ve çık
        if (!filters.selectedGroup && !filters.selectedAgeGroup) {
            message.warning('Lütfen grup veya yaş grubu seçiniz!');
            return;
        }

        // Tarih aralığı kontrolü
        if (!filters.dateRange || !filters.dateRange[0] || !filters.dateRange[1]) {
            message.warning('Lütfen geçerli bir tarih aralığı seçiniz!');
            return;
        }

        const params = {
            branchId: selectedBranch.id,
            startDate: filters.dateRange[0].toISOString(),
            endDate: filters.dateRange[1].toISOString(),
            groupId: filters.selectedGroup || null,
            ageGroupId: filters.selectedAgeGroup || null
        };

        try {
            await dispatch(fetchStudentPackageReportList(params));
            message.success('Rapor başarıyla yüklendi!');
        } catch (error) {
            console.error('Report load error:', error);
            message.error('Rapor yüklenirken bir hata oluştu!');
        }
    };

    // Filtre değişikliklerini izle ve raporu yükle
    useEffect(() => {
        if (selectedBranch && (filters.selectedGroup || filters.selectedAgeGroup) && filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            loadReport();
        }
        
        // Component unmount olduğunda state'i temizle
        return () => {
            dispatch(clearStudentPackageReport());
        };
    }, [selectedBranch, filters.selectedGroup, filters.selectedAgeGroup, filters.dateRange, dispatch]);

    const handleRefresh = () => {
        // Tarih aralığı kontrolü
        if (!filters.dateRange || !filters.dateRange[0] || !filters.dateRange[1]) {
            message.warning('Lütfen geçerli bir tarih aralığı seçiniz!');
            return;
        }
        loadReport();
    };

    // Excel indirme fonksiyonu
    const handleExportExcel = () => {
        // Tarih aralığı kontrolü
        if (!filters.dateRange || !filters.dateRange[0] || !filters.dateRange[1]) {
            message.warning('Lütfen geçerli bir tarih aralığı seçiniz!');
            return;
        }
        
        if (!studentPackageReport.list || studentPackageReport.list.length === 0) {
            message.warning('İndirilecek veri bulunmamaktadır!');
            return;
        }

        try {
            // Excel için veri hazırla
            const excelData = studentPackageReport.list.map(student => ({
                'Öğrenci Adı': student.studentFullName,
                'E-posta': student.studentEmail,
                'Toplam Paket': student.totalPackageCount,
                'Aktif Paket': student.activePackageCount,
                'Süresi Dolmuş Paket': student.expiredPackageCount,
                'Toplam Fiyat': student.totalPackagePrice,
                'Ödenen Tutar': student.totalPaidAmount,
                'Kalan Tutar': student.totalRemainingAmount,
                'Ödeme Tamamlanma Oranı (%)': student.paymentCompletionRate
            }));

            // Workbook oluştur
            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Aidat Raporu');

            // Dosya adı oluştur
            const fileName = `aidat_raporu_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;

            // Excel dosyasını indir
            XLSX.writeFile(wb, fileName);
            message.success('Excel dosyası başarıyla indirildi!');
        } catch (error) {
            console.error('Excel export error:', error);
            message.error('Excel dosyası indirilirken bir hata oluştu!');
        }
    };

    // Paket detayları için tablo kolonları
    const packageDetailColumns = [
        {
            title: 'Süre (Ay)',
            dataIndex: 'packagePlanDuration',
            key: 'packagePlanDuration',
            width: 100,
            render: (duration) => `${duration} Ay`,
        },
        {
            title: 'Paket Fiyatı',
            dataIndex: 'packagePrice',
            key: 'packagePrice',
            width: 120,
            render: (price) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {price.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Ödenen Tutar',
            dataIndex: 'packagePaidAmount',
            key: 'packagePaidAmount',
            width: 120,
            render: (amount) => (
                <Text style={{ color: '#52c41a' }}>
                    {amount.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Kalan Tutar',
            dataIndex: 'packageRemainingAmount',
            key: 'packageRemainingAmount',
            width: 120,
            render: (amount) => (
                <Text style={{ color: '#ff4d4f' }}>
                    {amount.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Bitiş Tarihi',
            dataIndex: 'packageEndTime',
            key: 'packageEndTime',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
        {
            title: 'Ödeme Durumu',
            dataIndex: 'packageRemainingAmount',
            key: 'paymentStatus',
            width: 120,
            render: (remainingAmount) => {
                const color = remainingAmount === 0 ? 'green' : 'red';
                const text = remainingAmount === 0 ? 'Ödendi' : 'Bekliyor';
                return <Tag color={color}>{text}</Tag>;
            },
        },
    ];

    // Ana tablo kolonları
    const columns = [
        {
            title: 'Öğrenci Adı',
            dataIndex: 'studentFullName',
            key: 'studentFullName',
            width: 200,
            fixed: 'left',
        },
        {
            title: 'Toplam Paket',
            dataIndex: 'totalPackageCount',
            key: 'totalPackageCount',
            width: 120,
            render: (value) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Toplam Fiyat',
            dataIndex: 'totalPackagePrice',
            key: 'totalPackagePrice',
            width: 130,
            render: (value) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Ödenen Tutar',
            dataIndex: 'totalPaidAmount',
            key: 'totalPaidAmount',
            width: 130,
            render: (value) => (
                <Text style={{ color: '#52c41a' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Kalan Tutar',
            dataIndex: 'totalRemainingAmount',
            key: 'totalRemainingAmount',
            width: 130,
            render: (value) => (
                <Text style={{ color: '#ff4d4f' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Ödeme Oranı',
            dataIndex: 'paymentCompletionRate',
            key: 'paymentCompletionRate',
            width: 150,
            render: (rate) => {
                const roundedRate = Math.round(Number(rate));
                return (
                    <div>
                        <Progress 
                            percent={roundedRate} 
                            size="small" 
                            status={roundedRate >= 80 ? 'success' : roundedRate >= 60 ? 'normal' : 'exception'}
                        />
                        <Text style={{ fontSize: '12px' }}>{roundedRate}%</Text>
                    </div>
                );
            },
        },
    ];

    // Açılabilir satır içeriği
    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '16px' }}>
                        {record.studentFullName} - Paket Detayları
                    </Text>
                </div>
                <Table
                    columns={packageDetailColumns}
                    dataSource={record.packages}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                />
            </div>
        );
    };

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Aidat raporunu görüntülemek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <div style={{ padding: '16px' }}>

            {/* Filtreler */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8}>
                        <Text strong>Tarih Aralığı:</Text>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) => setFilters(prev => ({ 
                                ...prev, 
                                dateRange: dates || [dayjs().subtract(1, 'year').startOf('month'), dayjs().endOf('month')]
                            }))}
                            style={{ width: '100%', marginTop: 8 }}
                        />
                    </Col>
                    <Col xs={24} sm={4}>
                        <Text strong>Grup:</Text>
                        <Select
                            placeholder="Grup seçin"
                            value={filters.selectedGroup}
                            onChange={(value) => setFilters(prev => ({ ...prev, selectedGroup: value }))}
                            allowClear
                            style={{ width: '100%', marginTop: 8 }}
                        >
                            {groups.map(group => (
                                <Option key={group.id} value={group.id}>{group.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={4}>
                        <Text strong>Yaş Grubu:</Text>
                        <Select
                            placeholder="Yaş grubu seçin"
                            value={filters.selectedAgeGroup}
                            onChange={(value) => setFilters(prev => ({ ...prev, selectedAgeGroup: value }))}
                            allowClear
                            style={{ width: '100%', marginTop: 8 }}
                        >
                            {ageGroups.map(ageGroup => (
                                <Option key={ageGroup.id} value={ageGroup.id}>{ageGroup.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', paddingTop: '32px' }}>
                            <Space>
                                <Button 
                                    type="primary" 
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={studentPackageReport.loading}
                                >
                                    Yenile
                                </Button>
                                <Button 
                                    type="default" 
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportExcel}
                                    disabled={!studentPackageReport.list.length}
                                >
                                    Excel İndir
                                </Button>
                            </Space>
                        </div>
                    </Col>
                </Row>
                
                {/* Hızlı Tarih Seçim Butonları */}
                <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                    <Col>
                        <Text strong style={{ marginRight: 8 }}>Hızlı Seçim:</Text>
                    </Col>
                    <Col>
                        <Space size="small" wrap>
                            <Button 
                                size="small" 
                                onClick={() => setFilters(prev => ({ 
                                    ...prev, 
                                    dateRange: [dayjs().startOf('month'), dayjs().endOf('month')] 
                                }))}
                                type={filters.dateRange?.[0]?.isSame(dayjs().startOf('month')) ? 'primary' : 'default'}
                            >
                                Bu Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setFilters(prev => ({ 
                                    ...prev, 
                                    dateRange: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] 
                                }))}
                                type={filters.dateRange?.[0]?.isSame(dayjs().subtract(1, 'month').startOf('month')) ? 'primary' : 'default'}
                            >
                                Geçen Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setFilters(prev => ({ 
                                    ...prev, 
                                    dateRange: [dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')] 
                                }))}
                                type={filters.dateRange?.[0]?.isSame(dayjs().subtract(3, 'month').startOf('month')) ? 'primary' : 'default'}
                            >
                                Son 3 Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setFilters(prev => ({ 
                                    ...prev, 
                                    dateRange: [dayjs().subtract(1, 'year').startOf('month'), dayjs().endOf('month')] 
                                }))}
                                type={filters.dateRange?.[0]?.isSame(dayjs().subtract(1, 'year').startOf('month')) ? 'primary' : 'default'}
                            >
                                Son 1 Yıl
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Hata mesajı */}
            {studentPackageReport.error && (
                <Card style={{ marginBottom: 16, borderColor: '#ff4d4f' }}>
                    <Text type="danger">
                        <CloseCircleOutlined /> {studentPackageReport.error}
                    </Text>
                </Card>
            )}

            {/* Uyarı mesajı - Filtre seçilmediğinde */}
            {!filters.selectedGroup && !filters.selectedAgeGroup && (
                <Card style={{ marginBottom: 16, borderColor: '#faad14' }}>
                    <Text type="warning">
                        <CalendarOutlined /> Lütfen grup veya yaş grubu seçerek raporu görüntüleyin.
                    </Text>
                </Card>
            )}

            {/* Detaylı Tablo */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={studentPackageReport.list}
                    rowKey="studentId"
                    expandable={{
                        expandedRowRender,
                        expandIcon: ({ expanded, onExpand, record }) => (
                            <Button
                                type="text"
                                size="small"
                                onClick={(e) => onExpand(record, e)}
                                icon={expanded ? <MinusOutlined /> : <PlusOutlined />}
                            >
                                {expanded ? 'Detayları Gizle' : 'Detayları Göster'}
                            </Button>
                        ),
                        rowExpandable: (record) => record.packages && record.packages.length > 0,
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} öğrenci`
                    }}
                    scroll={{ x: true }}
                    loading={studentPackageReport.loading}
                    locale={{
                        emptyText: studentPackageReport.loading ? <Spin /> : 
                            (!filters.selectedGroup && !filters.selectedAgeGroup) ? 
                            'Lütfen grup veya yaş grubu seçin' : 
                            'Henüz aidat verisi bulunmamaktadır'
                    }}
                />
            </Card>
        </div>
    );
};

export default PackageReport; 